import React from "react";
import {
  FaBuilding,
  FaSchool,
  FaArrowRight,
  FaGraduationCap,
  FaBookOpen,
  FaUsers,
  FaShieldAlt,
  FaHeart,
  FaStar,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";

import janaschool from "./smiling.jpg";
import schoolHistoryImg from "./boy.jpg";
import Footer from "./Footer";

const campusStyles = `
  .zpa-campus,
  .zpa-campus * {
    box-sizing: border-box;
  }

  .zpa-campus {
    --campus-black: #111111;
    --campus-black-light: #22211e;
    --campus-gold: #d5a62e;
    --campus-gold-light: #f2d78f;
    --campus-gold-pale: #fff8e8;
    --campus-text: #292929;
    --campus-muted: #686868;
    --campus-border: #ebe5d8;

    position: relative;
    overflow: hidden;
    color: var(--campus-text);
    background: #fff;
    font-family: inherit;
  }

  .zpa-campus img {
    max-width: 100%;
  }

  .zpa-campus a {
    color: inherit;
    text-decoration: none;
  }

  .zpa-campus .campus-container {
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
  }

  /* HERO */

  .zpa-campus .campus-hero {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 530px;
    min-height: clamp(420px, 49vw, 610px);
    overflow: hidden;
    background-color: #171717;
    background-position: center 45%;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .zpa-campus .campus-hero::before {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(
        90deg,
        rgba(10, 10, 10, 0.91) 0%,
        rgba(12, 12, 12, 0.76) 47%,
        rgba(12, 12, 12, 0.35) 100%
      );
    content: "";
  }

  .zpa-campus .campus-hero::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 6px;
    background: linear-gradient(
      90deg,
      #a77c1d,
      #f4d984 48%,
      #c09125
    );
    content: "";
  }

  .zpa-campus .campus-hero-pattern {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.18;
    background-image:
      linear-gradient(
        135deg,
        transparent 48%,
        rgba(255, 255, 255, 0.16) 49%,
        transparent 50%
      );
    background-size: 48px 48px;
    mask-image: linear-gradient(90deg, #000, transparent 75%);
  }

  .zpa-campus .campus-hero-inner {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 90px 28px 100px;
  }

  .zpa-campus .campus-hero-content {
    max-width: 760px;
  }

  .zpa-campus .campus-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    color: var(--campus-gold-light);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 2.7px;
    text-transform: uppercase;
  }

  .zpa-campus .campus-eyebrow-line {
    width: 35px;
    height: 2px;
    background: var(--campus-gold);
  }

  .zpa-campus .campus-school-label {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 19px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.7px;
    line-height: 1.7;
    text-transform: uppercase;
  }

  .zpa-campus .campus-school-label svg {
    flex-shrink: 0;
    color: var(--campus-gold-light);
    font-size: 18px;
  }

  .zpa-campus .campus-hero h1 {
    max-width: 700px;
    margin: 0;
    color: #fff;
    font-size: clamp(48px, 7vw, 82px);
    font-weight: 900;
    letter-spacing: -2.8px;
    line-height: 1.03;
  }

  .zpa-campus .campus-hero h1 span {
    display: block;
    margin-top: 8px;
    color: var(--campus-gold-light);
  }

  .zpa-campus .campus-hero-description {
    max-width: 560px;
    margin: 25px 0 0;
    color: rgba(255, 255, 255, 0.86);
    font-size: 15px;
    line-height: 1.9;
  }

  .zpa-campus .campus-hero-divider {
    width: 88px;
    height: 4px;
    margin-top: 30px;
    background: linear-gradient(
      90deg,
      var(--campus-gold),
      rgba(213, 166, 46, 0.15)
    );
  }

  .zpa-campus .campus-hero-bottom {
    position: absolute;
    z-index: 2;
    right: 28px;
    bottom: 32px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
  }

  .zpa-campus .campus-hero-bottom svg {
    color: var(--campus-gold-light);
    font-size: 15px;
  }

  /* INTRODUCTION */

  .zpa-campus .campus-information {
    position: relative;
    isolation: isolate;
    padding: 105px 25px 96px;
    background:
      radial-gradient(
        circle at 4% 20%,
        rgba(213, 166, 46, 0.08),
        transparent 25%
      ),
      linear-gradient(180deg, #fff 0%, #fcfbf8 100%);
  }

  .zpa-campus .campus-circle {
    position: absolute;
    z-index: -1;
    pointer-events: none;
    border: 1px solid rgba(213, 166, 46, 0.19);
    border-radius: 50%;
  }

  .zpa-campus .campus-circle-one {
    top: 70px;
    right: -130px;
    width: 280px;
    height: 280px;
  }

  .zpa-campus .campus-circle-two {
    bottom: 35px;
    left: -170px;
    width: 340px;
    height: 340px;
  }

  .zpa-campus .campus-information-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.98fr);
    align-items: center;
    gap: 78px;
    max-width: 1160px;
    margin: 0 auto;
  }

  /* CAMPUS IMAGE */

  .zpa-campus .campus-image-column {
    min-width: 0;
  }

  .zpa-campus .campus-image-card {
    position: relative;
    width: 100%;
    max-width: 530px;
    margin: 0 auto;
    padding: 0 18px 20px 0;
  }

  .zpa-campus .campus-image-frame {
    position: relative;
    z-index: 1;
    overflow: hidden;
    width: 100%;
    aspect-ratio: 4 / 4.4;
    border: 8px solid #fff;
    border-radius: 5px;
    background: #eee9df;
    box-shadow:
      0 22px 55px rgba(0, 0, 0, 0.13),
      0 0 0 1px rgba(213, 166, 46, 0.25);
  }

  .zpa-campus .campus-main-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.65s ease;
  }

  .zpa-campus .campus-image-card:hover .campus-main-image {
    transform: scale(1.035);
  }

  .zpa-campus .campus-image-frame::after {
    position: absolute;
    inset: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    content: "";
    pointer-events: none;
  }

  .zpa-campus .campus-image-decoration {
    position: absolute;
    pointer-events: none;
  }

  .zpa-campus .campus-image-decoration-one {
    top: -13px;
    right: 0;
    width: 53%;
    height: 48%;
    border-top: 5px solid var(--campus-gold);
    border-right: 5px solid var(--campus-gold);
  }

  .zpa-campus .campus-image-decoration-two {
    bottom: 0;
    left: -13px;
    width: 49%;
    height: 29%;
    background: var(--campus-black);
  }

  .zpa-campus .campus-image-badge {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 13px;
    width: calc(100% - 8px);
    max-width: 355px;
    min-height: 77px;
    margin: -59px 0 0 auto;
    padding: 16px 20px;
    color: #fff;
    background: linear-gradient(125deg, #111 0%, #28251e 100%);
    border-left: 4px solid var(--campus-gold);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.19);
  }

  .zpa-campus .campus-image-badge-icon {
    display: flex;
    flex: 0 0 43px;
    align-items: center;
    justify-content: center;
    width: 43px;
    height: 43px;
    color: #171717;
    background: linear-gradient(135deg, #f6e4ac, #d2a333);
    border-radius: 3px;
    font-size: 20px;
  }

  .zpa-campus .campus-image-badge-text {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .zpa-campus .campus-image-badge-text span {
    color: #d9d4c8;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  .zpa-campus .campus-image-badge-text strong {
    color: #fff;
    font-size: 15px;
    font-weight: 800;
    line-height: 1.4;
  }

  /* INFORMATION TEXT */

  .zpa-campus .campus-content {
    min-width: 0;
  }

  .zpa-campus .campus-section-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    color: #96701c;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  .zpa-campus .campus-section-label::before {
    width: 28px;
    height: 2px;
    background: var(--campus-gold);
    content: "";
  }

  .zpa-campus .campus-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 57px;
    height: 57px;
    margin-bottom: 23px;
    color: #a47a1b;
    background: var(--campus-gold-pale);
    border: 1px solid #efe1bc;
    border-radius: 4px;
    font-size: 24px;
  }

  .zpa-campus .campus-content h2 {
    margin: 0;
    color: var(--campus-black);
    font-size: clamp(34px, 4.2vw, 49px);
    font-weight: 900;
    letter-spacing: -1.5px;
    line-height: 1.16;
  }

  .zpa-campus .campus-content h2 span {
    display: block;
    color: #ad8324;
  }

  .zpa-campus .campus-content-rule {
    width: 65px;
    height: 4px;
    margin: 23px 0;
    background: var(--campus-gold);
  }

  .zpa-campus .campus-content p {
    margin: 0 0 17px;
    color: var(--campus-muted);
    font-size: 13.5px;
    line-height: 2;
  }

  .zpa-campus .campus-highlights {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 26px;
  }

  .zpa-campus .campus-highlight {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
    padding: 15px 13px;
    border: 1px solid var(--campus-border);
    border-radius: 4px;
    background: #fff;
    transition:
      transform 0.25s ease,
      border-color 0.25s ease,
      box-shadow 0.25s ease;
  }

  .zpa-campus .campus-highlight:hover {
    transform: translateY(-3px);
    border-color: #d9bd78;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.045);
  }

  .zpa-campus .campus-highlight-icon {
    display: flex;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    color: #99731d;
    background: var(--campus-gold-pale);
    border-radius: 3px;
    font-size: 13px;
  }

  .zpa-campus .campus-highlight-text {
    min-width: 0;
  }

  .zpa-campus .campus-highlight-text strong {
    display: block;
    margin: 2px 0 5px;
    color: #222;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.5;
  }

  .zpa-campus .campus-highlight-text span {
    display: block;
    color: #777;
    font-size: 10px;
    line-height: 1.7;
  }

  .zpa-campus .campus-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 13px;
    min-height: 49px;
    margin-top: 27px;
    padding: 14px 22px;
    color: #fff;
    background: var(--campus-black);
    border: 1px solid var(--campus-black);
    border-radius: 3px;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 800;
    transition:
      color 0.25s ease,
      background 0.25s ease,
      border-color 0.25s ease,
      transform 0.25s ease;
  }

  .zpa-campus .campus-btn svg {
    color: var(--campus-gold-light);
    transition: transform 0.25s ease;
  }

  .zpa-campus .campus-btn:hover {
    color: #111;
    background: var(--campus-gold);
    border-color: var(--campus-gold);
    transform: translateY(-2px);
  }

  .zpa-campus .campus-btn:hover svg {
    color: #111;
    transform: translateX(4px);
  }

  .zpa-campus .campus-btn:focus-visible {
    outline: 3px solid rgba(213, 166, 46, 0.55);
    outline-offset: 4px;
  }

  /* BOTTOM COMMITMENT */

  .zpa-campus .campus-commitment {
    padding: 0 25px 85px;
    background: #fcfbf8;
  }

  .zpa-campus .campus-commitment-inner {
    display: grid;
    grid-template-columns: 1.2fr repeat(3, minmax(0, 1fr));
    align-items: center;
    max-width: 1160px;
    margin: 0 auto;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(115deg, #111 0%, #25231e 100%);
    border-radius: 5px;
  }

  .zpa-campus .campus-commitment-intro {
    padding: 29px 26px;
  }

  .zpa-campus .campus-commitment-intro span {
    display: block;
    margin-bottom: 9px;
    color: var(--campus-gold-light);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.8px;
    text-transform: uppercase;
  }

  .zpa-campus .campus-commitment-intro h3 {
    margin: 0;
    color: #fff;
    font-size: 19px;
    font-weight: 800;
    line-height: 1.5;
  }

  .zpa-campus .campus-commitment-item {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 78px;
    padding: 18px;
    border-left: 1px solid rgba(255, 255, 255, 0.13);
  }

  .zpa-campus .campus-commitment-item svg {
    flex-shrink: 0;
    color: var(--campus-gold-light);
    font-size: 19px;
  }

  .zpa-campus .campus-commitment-item span {
    color: #f3f0e7;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.7;
  }

  @media (min-width: 1500px) {
    .zpa-campus .campus-hero-inner {
      max-width: 1300px;
    }

    .zpa-campus .campus-container {
      max-width: 1300px;
    }

    .zpa-campus .campus-information-grid,
    .zpa-campus .campus-commitment-inner {
      max-width: 1200px;
    }

    .zpa-campus .campus-information-grid {
      gap: 95px;
    }
  }

  @media (max-width: 1000px) {
    .zpa-campus .campus-information {
      padding-top: 80px;
      padding-bottom: 75px;
    }

    .zpa-campus .campus-information-grid {
      gap: 42px;
    }

    .zpa-campus .campus-content h2 {
      font-size: 37px;
    }

    .zpa-campus .campus-commitment-inner {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .zpa-campus .campus-commitment-intro {
      grid-column: 1 / -1;
      padding: 23px 25px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.13);
    }

    .zpa-campus .campus-commitment-item:first-of-type {
      border-left: none;
    }
  }

  @media (max-width: 760px) {
    .zpa-campus .campus-hero {
      min-height: 460px;
      background-position: center;
    }

    .zpa-campus .campus-hero-inner {
      padding: 75px 23px 90px;
    }

    .zpa-campus .campus-hero h1 {
      font-size: clamp(43px, 9vw, 64px);
      letter-spacing: -1.8px;
    }

    .zpa-campus .campus-hero-description {
      max-width: 480px;
      font-size: 13px;
    }

    .zpa-campus .campus-hero-bottom {
      right: 20px;
      bottom: 25px;
      font-size: 9px;
    }

    .zpa-campus .campus-information {
      padding: 65px 20px 65px;
    }

    .zpa-campus .campus-information-grid {
      grid-template-columns: 1fr;
      gap: 44px;
    }

    .zpa-campus .campus-image-card {
      max-width: 490px;
    }

    .zpa-campus .campus-image-frame {
      aspect-ratio: 4 / 3.5;
    }

    .zpa-campus .campus-content {
      max-width: 620px;
      margin: 0 auto;
    }

    .zpa-campus .campus-content h2 {
      font-size: clamp(34px, 6vw, 45px);
    }

    .zpa-campus .campus-commitment {
      padding: 0 20px 65px;
    }
  }

  @media (max-width: 480px) {
    .zpa-campus .campus-hero {
      min-height: 410px;
      background-position: 54% center;
    }

    .zpa-campus .campus-hero::before {
      background: linear-gradient(
        90deg,
        rgba(10, 10, 10, 0.89),
        rgba(10, 10, 10, 0.62)
      );
    }

    .zpa-campus .campus-hero-inner {
      padding: 63px 18px 76px;
    }

    .zpa-campus .campus-school-label {
      align-items: flex-start;
      font-size: 9px;
      letter-spacing: 1px;
    }

    .zpa-campus .campus-eyebrow {
      gap: 8px;
      font-size: 9px;
      letter-spacing: 1.8px;
    }

    .zpa-campus .campus-eyebrow-line {
      width: 24px;
    }

    .zpa-campus .campus-hero h1 {
      font-size: 43px;
      letter-spacing: -1.5px;
    }

    .zpa-campus .campus-hero-description {
      margin-top: 19px;
      font-size: 12px;
      line-height: 1.8;
    }

    .zpa-campus .campus-hero-bottom {
      right: 14px;
      bottom: 22px;
      gap: 7px;
      font-size: 8px;
      letter-spacing: 0.8px;
    }

    .zpa-campus .campus-information {
      padding: 51px 15px 53px;
    }

    .zpa-campus .campus-image-card {
      padding-right: 12px;
    }

    .zpa-campus .campus-image-frame {
      aspect-ratio: 4 / 4.1;
      border-width: 6px;
    }

    .zpa-campus .campus-image-decoration-one {
      top: -9px;
      border-width: 4px;
    }

    .zpa-campus .campus-image-decoration-two {
      left: -8px;
    }

    .zpa-campus .campus-image-badge {
      gap: 10px;
      min-height: 66px;
      margin-top: -48px;
      padding: 13px;
    }

    .zpa-campus .campus-image-badge-icon {
      flex-basis: 36px;
      width: 36px;
      height: 36px;
      font-size: 17px;
    }

    .zpa-campus .campus-image-badge-text span {
      font-size: 8px;
      letter-spacing: 1px;
    }

    .zpa-campus .campus-image-badge-text strong {
      font-size: 12px;
    }

    .zpa-campus .campus-section-icon {
      width: 49px;
      height: 49px;
      margin-bottom: 19px;
      font-size: 21px;
    }

    .zpa-campus .campus-section-label {
      font-size: 9px;
      letter-spacing: 1.5px;
    }

    .zpa-campus .campus-content h2 {
      font-size: 33px;
      letter-spacing: -0.8px;
    }

    .zpa-campus .campus-content p {
      font-size: 12.5px;
      line-height: 1.9;
    }

    .zpa-campus .campus-highlights {
      gap: 9px;
    }

    .zpa-campus .campus-highlight {
      gap: 8px;
      padding: 12px 9px;
    }

    .zpa-campus .campus-highlight-icon {
      flex-basis: 27px;
      width: 27px;
      height: 27px;
      font-size: 12px;
    }

    .zpa-campus .campus-highlight-text strong {
      font-size: 10px;
    }

    .zpa-campus .campus-highlight-text span {
      font-size: 9px;
    }

    .zpa-campus .campus-btn {
      width: 100%;
    }

    .zpa-campus .campus-commitment {
      padding: 0 15px 50px;
    }

    .zpa-campus .campus-commitment-inner {
      grid-template-columns: 1fr;
    }

    .zpa-campus .campus-commitment-intro {
      padding: 22px 19px;
    }

    .zpa-campus .campus-commitment-intro h3 {
      font-size: 18px;
    }

    .zpa-campus .campus-commitment-item {
      min-height: 57px;
      padding: 15px 19px;
      border-left: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .zpa-campus .campus-commitment-item:last-child {
      border-bottom: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .zpa-campus *,
    .zpa-campus *::before,
    .zpa-campus *::after {
      scroll-behavior: auto !important;
      transition-duration: 0.01ms !important;
      animation-duration: 0.01ms !important;
    }
  }
`;

const CampusPage = () => {
  const handleExploreCampus = () => {
    document.getElementById("campus-highlights")?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <>
      <style>{campusStyles}</style>

      <main className="zpa-campus">
        {/* Hero section */}
        <section
          className="campus-hero"
          style={{ backgroundImage: `url(${janaschool})` }}
          aria-label="Zion Praise Academy campus introduction"
        >
          <div className="campus-hero-pattern" aria-hidden="true" />

          <div className="campus-hero-inner">
            <motion.div
              className="campus-hero-content"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="campus-school-label">
                <FaGraduationCap />
                <span>Zion Praise Academy Senior High School</span>
              </div>

              <div className="campus-eyebrow">
                <span className="campus-eyebrow-line" />
                Our Environment
              </div>

              <h1>
                Our
                <span>Campus</span>
              </h1>

              <p className="campus-hero-description">
                A welcoming place where learning, innovation, personal growth,
                and academic excellence come together to help students build a
                brighter future.
              </p>

              <div className="campus-hero-divider" />
            </motion.div>
          </div>

          <div className="campus-hero-bottom">
            <FaMapMarkerAlt />
            <span>A place to learn and grow</span>
          </div>
        </section>

        {/* Campus information */}
        <section className="campus-information">
          <div
            className="campus-circle campus-circle-one"
            aria-hidden="true"
          />
          <div
            className="campus-circle campus-circle-two"
            aria-hidden="true"
          />

          <div className="campus-information-grid">
            <motion.div
              className="campus-image-column"
              initial={{ opacity: 0, x: -45 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.75, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="campus-image-card">
                <div className="campus-image-frame">
                  <img
                    src={schoolHistoryImg}
                    alt="Students at Zion Praise Academy"
                    className="campus-main-image"
                    loading="lazy"
                  />
                </div>

                <div
                  className="campus-image-decoration campus-image-decoration-one"
                  aria-hidden="true"
                />
                <div
                  className="campus-image-decoration campus-image-decoration-two"
                  aria-hidden="true"
                />

                <div className="campus-image-badge">
                  <div className="campus-image-badge-icon">
                    <FaSchool />
                  </div>

                  <div className="campus-image-badge-text">
                    <span>Our Learning Environment</span>
                    <strong>Growing Together</strong>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="campus-content"
              initial={{ opacity: 0, x: 45 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.75, ease: "easeOut", delay: 0.08 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="campus-section-label">
                Welcome to Our School
              </div>

              <div className="campus-section-icon">
                <FaBuilding />
              </div>

              <h2>
                A Campus Designed
                <span>For Excellence</span>
              </h2>

              <div className="campus-content-rule" />

              <p>
                Nestled in a serene and welcoming environment, our campus
                provides a setting that encourages learning, creativity,
                confidence, and personal growth.
              </p>

              <p>
                From comfortable classrooms to supportive academic spaces, we
                strive to create an atmosphere where students feel encouraged
                to explore their abilities, develop their talents, and work
                toward their full potential.
              </p>

              <div className="campus-highlights" id="campus-highlights">
                <div className="campus-highlight">
                  <div className="campus-highlight-icon">
                    <FaBookOpen />
                  </div>

                  <div className="campus-highlight-text">
                    <strong>Learning Focus</strong>
                    <span>Supporting students' academic development.</span>
                  </div>
                </div>

                <div className="campus-highlight">
                  <div className="campus-highlight-icon">
                    <FaUsers />
                  </div>

                  <div className="campus-highlight-text">
                    <strong>Student Growth</strong>
                    <span>Encouraging confidence and collaboration.</span>
                  </div>
                </div>

                <div className="campus-highlight">
                  <div className="campus-highlight-icon">
                    <FaShieldAlt />
                  </div>

                  <div className="campus-highlight-text">
                    <strong>Positive Values</strong>
                    <span>Promoting respect and responsibility.</span>
                  </div>
                </div>

                <div className="campus-highlight">
                  <div className="campus-highlight-icon">
                    <FaStar />
                  </div>

                  <div className="campus-highlight-text">
                    <strong>Striving for Excellence</strong>
                    <span>Inspiring effort and continuous improvement.</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="campus-btn"
                onClick={handleExploreCampus}
              >
                <span>Explore Campus Features</span>
                <FaArrowRight />
              </button>
            </motion.div>
          </div>
        </section>

        {/* School commitment banner */}
        <section className="campus-commitment">
          <div className="campus-commitment-inner">
            <div className="campus-commitment-intro">
              <span>Our Commitment</span>
              <h3>A foundation for learning, character, and opportunity.</h3>
            </div>

            <div className="campus-commitment-item">
              <FaGraduationCap />
              <span>Academic Development</span>
            </div>

            <div className="campus-commitment-item">
              <FaHeart />
              <span>Student Well-being</span>
            </div>

            <div className="campus-commitment-item">
              <FaStar />
              <span>Excellence &amp; Growth</span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default CampusPage;
